import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useHistory } from "react-router-dom";
import PageLayout from "../../components/PageLayout";
import Button from "../../components/Button";
import { getLoginDetails } from "../../utils/storage";
import { getData, putData, GET_USER_VIRTUAL_GRADING_ITEMS_URL, GET_VIRTUAL_GRADING_INSIGHTS_URL, GET_VIRTUAL_GRADING_ITEM_DETAILS_URL, UPDATE_VIRTUAL_GRADING_ITEM_URL, GET_CONVERTERS_URL } from "../../utils/api";
import UploadIcon from "../../icons/upload.svg";
import CaretIcon from "../../icons/rev_carat.png";
import RefreshIcon from "../../icons/refresh.svg";
import ImageLightbox from "../../components/VirtualGrading/ImageLightbox";
import "./VirtualGrading.css";

// Mock data for admin dashboard
const mockAdminData = {
  pendingQueue: 3,
  itemsGraded: 2,
  totalEarnings: 11,
  avgTime: "15 mins",
  recentItems: [
    {
      id: "ITEM00003",
      title: "Toyota Camry Catalytic Converter",
      make: "Toyota",
      model: "Camry",
      year: "2018",
      partNumber: "TC-CC-2018-001",
      user: "John Doe",
      submittedDate: "2024-10-28T19:16:00Z",
      status: "pending",
      priority: "high",
      image: "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=400&h=200&fit=crop&crop=center",
    },
    {
      id: "ITEM00004",
      title: "Honda Civic Exhaust System",
      make: "Honda",
      model: "Civic",
      year: "2020",
      partNumber: "HC-ES-2020-002",
      user: "Sarah Wilson",
      submittedDate: "2024-10-28T18:45:00Z",
      status: "pending",
      priority: "medium",
      image: "https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=400&h=200&fit=crop&crop=center",
    },
    {
      id: "ITEM00005",
      title: "Ford F-150 Muffler Assembly",
      make: "Ford",
      model: "F-150",
      year: "2019",
      partNumber: "FF-MA-2019-003",
      user: "Mike Johnson",
      submittedDate: "2024-10-28T17:30:00Z",
      status: "pending",
      priority: "low",
      image: "https://images.unsplash.com/photo-1609521263047-f8f205d0f3c1?w=400&h=200&fit=crop&crop=center",
    },
  ],
};


const AdminDashboard = () => {
  const { hierarchy } = getLoginDetails();
  const history = useHistory();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    graded: 0,
    rejected: 0,
    totalValue: 0,
    avgPrice: 0,
  });
  const [filterStatus, setFilterStatus] = useState("pending");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [expandedItem, setExpandedItem] = useState(null);
  const [gradingData, setGradingData] = useState({
    assessedValue: "",
    gradingNotes: "",
    grade: "A",
  });
  const [priceInput, setPriceInput] = useState("");
  const [isGrading, setIsGrading] = useState(false);
  const [gradeInput, setGradeInput] = useState("");
  const [gradeSuggestions, setGradeSuggestions] = useState([]);
  const [showGradeSuggestions, setShowGradeSuggestions] = useState(false);
  const [selectedGradeItem, setSelectedGradeItem] = useState(null);
  const [isLoadingGradeSuggestions, setIsLoadingGradeSuggestions] = useState(false);
  const gradeInputRef = useRef(null);
  const gradeSearchTimerRef = useRef(null);
  const scrollTimerRef = useRef(null);
  const expandedGradingFormRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [params, setParams] = useState({
    limit: 10,
    offset: 0,
    areMoreItemsAvailable: false,
  });
  const scrollContainerRef = useRef(null);

  const getItemImageUrl = (item) =>
    item.itemURL || item.itemUrl || item.image || item.images?.[0] || "https://via.placeholder.com/400x300";

  const openImagePreview = (e, item) => {
    e.stopPropagation();
    setPreviewImage({
      src: getItemImageUrl(item),
      alt: item.title || "Item",
    });
  };
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLoadingPrevious, setIsLoadingPrevious] = useState(false);
  const [hasMorePrevious, setHasMorePrevious] = useState(false);
  const fetchingMoreRef = useRef(false);
  const fetchingPreviousRef = useRef(false);

  // Redirect if not admin
  useEffect(() => {
    if (hierarchy !== 1) {
      // Redirect to appropriate page
      window.location.href = "/virtual-grading";
    }
  }, [hierarchy]);

  // Map status to API status codes: pending=1, graded=2, rejected=3, All=0
  const getStatusCode = (tab) => {
    const statusMap = {
      pending: 1,
      graded: 2,
      rejected: 3,
      all: 0,
    };
    return statusMap[tab] || 0;
  };

  // Calculate time elapsed from createdDate
  const getTimeElapsed = (createdDate) => {
    if (!createdDate) return "Unknown";
    
    const now = new Date();
    const created = new Date(createdDate);
    const diffInMs = now - created;
    const diffInSeconds = Math.floor(diffInMs / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    const diffInWeeks = Math.floor(diffInDays / 7);
    const diffInMonths = Math.floor(diffInDays / 30);
    const diffInYears = Math.floor(diffInDays / 365);

    if (diffInSeconds < 60) {
      return "Just now";
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes !== 1 ? "s" : ""} ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours !== 1 ? "s" : ""} ago`;
    } else if (diffInDays < 7) {
      return `${diffInDays} day${diffInDays !== 1 ? "s" : ""} ago`;
    } else if (diffInWeeks < 4) {
      return `${diffInWeeks} week${diffInWeeks !== 1 ? "s" : ""} ago`;
    } else if (diffInMonths < 12) {
      return `${diffInMonths} month${diffInMonths !== 1 ? "s" : ""} ago`;
    } else {
      return `${diffInYears} year${diffInYears !== 1 ? "s" : ""} ago`;
    }
  };

  // Convert date to epoch milliseconds - following pattern from CostSheetReport
  // For "from" date: start of day (00:00:00.000 AM)
  // For "to" date: end of day (23:59:59.999 PM)
  const getDateEpoch = (dateValue, isEndOfDay = false) => {
    if (!dateValue) return null;
    
    if (isEndOfDay) {
      // Set to end of day: 23:59:59.999 PM
      const endDt = new Date(dateValue).setHours(23, 59, 59, 999);
      return new Date(endDt).getTime();
    } else {
      // Set to start of day: 00:00:00.000 AM
      const startDt = new Date(dateValue).setHours(0, 0, 0, 0);
      return new Date(startDt).getTime();
    }
  };

  // Fetch items from API with pagination
  const fetchItems = async (status = null, resetOffset = false, append = false, prepend = false) => {
    if (!append && !prepend) {
      setIsLoading(true);
    }
    
    try {
      const statusCode = status !== null ? getStatusCode(status) : getStatusCode(filterStatus);
      
      const apiParams = {
        count: params.limit,
        offset: resetOffset ? 0 : params.offset,
        status: statusCode,
      };

      // Add date filters if provided - convert to epoch milliseconds
      if (fromDate) {
        apiParams.fromDate = getDateEpoch(fromDate, false);
      }
      if (toDate) {
        apiParams.toDate = getDateEpoch(toDate, true);
      }

      const response = await getData(GET_USER_VIRTUAL_GRADING_ITEMS_URL, apiParams);
      
      // Handle API response - API returns { list: [...], areMoreItemsAvailable: boolean }
      const currentOffset = resetOffset ? 0 : params.offset;
      
      if (response && Array.isArray(response.list)) {
        if (append) {
          // Append new items at the end
          setItems(prev => [...prev, ...response.list]);
          setParams(prev => ({
            ...prev,
            offset: prev.offset + response.list.length,
            areMoreItemsAvailable: response.areMoreItemsAvailable || false,
          }));
        } else if (prepend) {
          // Prepend new items at the beginning
          setItems(prev => [...response.list, ...prev]);
          setParams(prev => ({
            ...prev,
            offset: Math.max(0, prev.offset - response.list.length),
            areMoreItemsAvailable: prev.areMoreItemsAvailable,
          }));
          // Check if we can load more previous items
          setHasMorePrevious(currentOffset > 0);
        } else {
          // Replace items (initial load or reset)
          setItems(response.list);
          setParams(prev => ({
            ...prev,
            offset: currentOffset,
            areMoreItemsAvailable: response.areMoreItemsAvailable || false,
          }));
          setHasMorePrevious(false);
        }
      } else if (Array.isArray(response)) {
        if (append) {
          setItems(prev => [...prev, ...response]);
          setParams(prev => ({
            ...prev,
            offset: prev.offset + response.length,
            areMoreItemsAvailable: false,
          }));
        } else if (prepend) {
          setItems(prev => [...response, ...prev]);
          setParams(prev => ({
            ...prev,
            offset: Math.max(0, prev.offset - response.length),
            areMoreItemsAvailable: false,
          }));
          setHasMorePrevious(currentOffset > 0);
        } else {
          setItems(response);
          setParams(prev => ({
            ...prev,
            offset: currentOffset,
            areMoreItemsAvailable: false,
          }));
          setHasMorePrevious(false);
        }
      } else {
        if (!append && !prepend) {
          setItems([]);
          setParams(prev => ({
            ...prev,
            offset: currentOffset,
            areMoreItemsAvailable: false,
          }));
          setHasMorePrevious(false);
        }
      }
    } catch (error) {
      console.error("Error fetching items:", error);
        if (!append && !prepend) {
          setItems([]);
          setParams(prev => ({
            ...prev,
            areMoreItemsAvailable: false,
          }));
          setHasMorePrevious(false);
        }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
      setIsLoadingPrevious(false);
    }
  };

  // Fetch statistics using insights API
  const fetchStats = async () => {
    try {
      const params = {};

      // Add date filters if provided - convert to epoch milliseconds
      if (fromDate) {
        params.fromDate = getDateEpoch(fromDate, false);
      }
      if (toDate) {
        params.toDate = getDateEpoch(toDate, true);
      }

      const response = await getData(GET_VIRTUAL_GRADING_INSIGHTS_URL, params);

      // API response: { pending, graded, value, avg, rejected }
      const insights = response || {};
      
      // Calculate total items from insights
      const total = (insights.pending || 0) + (insights.graded || 0) + (insights.rejected || 0);

      setStats({
        total: total,
        pending: insights.pending || 0,
        graded: insights.graded || 0,
        rejected: insights.rejected || 0,
        totalValue: insights.value || 0,
        avgPrice: insights.avg || 0,
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
      setStats({
        total: 0,
        pending: 0,
        graded: 0,
        rejected: 0,
        totalValue: 0,
        avgPrice: 0,
      });
    }
  };

  const getStatusColor = (status) => {
    // Status can be number (1, 2, 3) or string
    const statusStr = typeof status === 'number' 
      ? (status === 1 ? "pending" : status === 2 ? "graded" : status === 3 ? "rejected" : "pending")
      : status;
    switch (statusStr) {
      case "pending":
        return "#ffc107";
      case "graded":
        return "#28a745";
      case "rejected":
        return "#dc3545";
      default:
        return "#6c757d";
    }
  };

  // Grading functionality
  const handleStartGrading = (itemId) => {
    // Toggle: if already expanded, close it; otherwise, expand it
    if (expandedItem === itemId) {
      // Close the form
      setExpandedItem(null);
      setGradingData({
        assessedValue: "",
        gradingNotes: "",
        grade: "A",
      });
      setPriceInput("");
      setGradeInput("");
      setGradeSuggestions([]);
      setShowGradeSuggestions(false);
      setSelectedGradeItem(null);
    } else {
      // Open the form
      setExpandedItem(itemId);
      setGradingData({
        assessedValue: "",
        gradingNotes: "",
        grade: "A",
      });
      setPriceInput("");
      setGradeInput("");
      setGradeSuggestions([]);
      setShowGradeSuggestions(false);
      setSelectedGradeItem(null);
    }
  };

  const handleCloseGrading = () => {
    setExpandedItem(null);
    setGradingData({
      assessedValue: "",
      gradingNotes: "",
      grade: "A",
    });
    setPriceInput("");
    setGradeInput("");
    setGradeSuggestions([]);
    setShowGradeSuggestions(false);
    setSelectedGradeItem(null);
  };

  const handleGradingChange = (field, value) => {
    setGradingData({
      ...gradingData,
      [field]: value,
    });
  };

  // Price search functionality
  const handlePriceInputChange = (value) => {
    setPriceInput(value);
    setGradingData({
      ...gradingData,
      assessedValue: value,
    });
    // Clear Grade when Assessed Value is entered
    if (value && value.trim() !== "") {
      setGradeInput("");
      setSelectedGradeItem(null);
      setGradingData({
        ...gradingData,
        assessedValue: value,
        grade: "",
      });
    }
  };

  // Grade search functionality using product getAll API
  const searchGradeProducts = async (query) => {
    if (!query || query.length === 0) {
      setGradeSuggestions([]);
      setShowGradeSuggestions(false);
      return;
    }

    setIsLoadingGradeSuggestions(true);
    
    try {
      // Get companyId and facilityCity from the current item being graded
      const currentItem = items.find(item => item.itemId === expandedItem || item.id === expandedItem);
      const companyId = currentItem?.companyId || "";
      const facilityCity = currentItem?.facilityCity || "";

      // Call product getAll API similar to cart detail screen
      const data = await getData(GET_CONVERTERS_URL, {
        count: 50,
        offset: 0,
        text: query,
        isLoad: true,
        companyId: companyId,
        facilityCity: facilityCity,
      });

      setGradeSuggestions(data.list || []);
      setShowGradeSuggestions(true);
      setIsLoadingGradeSuggestions(false);
    } catch (error) {
      console.error("Error searching grade products:", error);
      setGradeSuggestions([]);
      setShowGradeSuggestions(false);
      setIsLoadingGradeSuggestions(false);
    }
  };

  const handleGradeInputChange = (value) => {
    setGradeInput(value);
    setGradingData({
      ...gradingData,
      grade: value,
    });
    // Clear Assessed Value when Grade is entered
    if (value && value.trim() !== "") {
      setPriceInput("");
      setGradingData({
        ...gradingData,
        assessedValue: "",
        grade: value,
      });
    }
    
    // Debounce search - call API after 500ms of no typing
    if (gradeSearchTimerRef.current) {
      clearTimeout(gradeSearchTimerRef.current);
    }
    
    gradeSearchTimerRef.current = setTimeout(() => {
      if (value.length > 0) {
        searchGradeProducts(value);
      } else {
        setGradeSuggestions([]);
        setShowGradeSuggestions(false);
      }
    }, 500);
  };

  const handleGradeItemSelect = (item) => {
    setSelectedGradeItem(item);
    setGradeInput(item.oem || "");
    setGradingData({
      ...gradingData,
      grade: item.oem || "",
    });
    // Clear Assessed Value when Grade is selected (mutually exclusive)
    setPriceInput("");
    setGradingData({
      ...gradingData,
      grade: item.oem || "",
      assessedValue: "",
    });
    setShowGradeSuggestions(false);
  };

  const handleClearGrade = () => {
    setSelectedGradeItem(null);
    setGradeInput("");
    setGradingData({
      ...gradingData,
      grade: "",
    });
    setShowGradeSuggestions(false);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (gradeSearchTimerRef.current) {
        clearTimeout(gradeSearchTimerRef.current);
      }
    };
  }, []);


  const handleSubmitGrade = async (itemId) => {
    // Validate that at least one of Assessed Value or Grade is provided
    const hasAssessedValue = priceInput && priceInput.trim() !== "";
    const hasGrade = selectedGradeItem !== null || (gradeInput && gradeInput.trim() !== "");
    
    if (!hasAssessedValue && !hasGrade) {
      alert("Please provide either Assessed Value ($) or Grade");
      return;
    }
    
    setIsGrading(true);
    
    try {

      // Get price: if Grade is selected, use basePrice; otherwise use assessedValue/priceInput
      const price = selectedGradeItem?.basePrice 
        ? selectedGradeItem.basePrice
        : (parseFloat(gradingData.assessedValue) || parseFloat(priceInput) || 0);

      // Get grade: if Grade is selected, use oem; otherwise empty (since we have assessedValue)
      const grade = selectedGradeItem?.oem || (gradeInput && gradeInput.trim() !== "" ? gradeInput : "") || gradingData.grade || "";

      // Get productId from selectedGradeItem (only if grade is selected)
      const productId = selectedGradeItem?.productId || "";

      // Call update item API to submit the grade
      await putData(UPDATE_VIRTUAL_GRADING_ITEM_URL, {
        itemId: itemId,
        status: 2, // 2 = graded
        price: price,
        adminNotes: gradingData.gradingNotes || "",
        grade: grade,
        productId: productId,
      });

      // Close the grading form
      setExpandedItem(null);
      
      // Reset grading form data
      setGradingData({
        assessedValue: "",
        gradingNotes: "",
        grade: "A",
      });
      setPriceInput("");
      setGradeInput("");
      setGradeSuggestions([]);
      setShowGradeSuggestions(false);
      setSelectedGradeItem(null);

      // Refetch items and stats
      fetchItems(filterStatus, false);
      fetchStats();
      
      setIsGrading(false);
    } catch (error) {
      console.error("Error submitting grade:", error);
      setIsGrading(false);
      // TODO: Show error toast/notification
    }
  };

  // Infinite scroll functions
  const fetchMoreItems = useCallback(async () => {
    if (isLoadingMore || !params.areMoreItemsAvailable || isLoading || fetchingMoreRef.current) return;
    
    fetchingMoreRef.current = true;
    setIsLoadingMore(true);
    const newOffset = params.offset + params.limit;
    
    try {
      const statusCode = getStatusCode(filterStatus);
      const apiParams = {
        count: params.limit,
        offset: newOffset,
        status: statusCode,
      };

      if (fromDate) {
        apiParams.fromDate = getDateEpoch(fromDate, false);
      }
      if (toDate) {
        apiParams.toDate = getDateEpoch(toDate, true);
      }

      const response = await getData(GET_USER_VIRTUAL_GRADING_ITEMS_URL, apiParams);
      
      if (response && Array.isArray(response.list)) {
        // Deduplicate items by itemId or itemSeq
        setItems(prev => {
          const existingIds = new Set(prev.map(item => item.itemId || item.itemSeq || item.id));
          const newItems = response.list.filter(item => {
            const itemId = item.itemId || item.itemSeq || item.id;
            return !existingIds.has(itemId);
          });
          return [...prev, ...newItems];
        });
        setParams(prev => ({
          ...prev,
          offset: newOffset,
          areMoreItemsAvailable: response.areMoreItemsAvailable || false,
        }));
      }
    } catch (error) {
      console.error("Error fetching more items:", error);
    } finally {
      setIsLoadingMore(false);
      fetchingMoreRef.current = false;
    }
  }, [params.offset, params.limit, params.areMoreItemsAvailable, isLoadingMore, isLoading, filterStatus, fromDate, toDate]);

  const fetchPreviousItems = useCallback(async () => {
    if (isLoadingPrevious || params.offset === 0 || isLoading || fetchingPreviousRef.current) return;
    
    fetchingPreviousRef.current = true;
    setIsLoadingPrevious(true);
    const newOffset = Math.max(0, params.offset - params.limit);
    const container = scrollContainerRef.current;
    const previousScrollHeight = container ? container.scrollHeight : 0;
    
    try {
      const statusCode = getStatusCode(filterStatus);
      const apiParams = {
        count: params.limit,
        offset: newOffset,
        status: statusCode,
      };

      if (fromDate) {
        apiParams.fromDate = getDateEpoch(fromDate, false);
      }
      if (toDate) {
        apiParams.toDate = getDateEpoch(toDate, true);
      }

      const response = await getData(GET_USER_VIRTUAL_GRADING_ITEMS_URL, apiParams);
      
      if (response && Array.isArray(response.list)) {
        // Deduplicate items by itemId or itemSeq
        setItems(prev => {
          const existingIds = new Set(prev.map(item => item.itemId || item.itemSeq || item.id));
          const newItems = response.list.filter(item => {
            const itemId = item.itemId || item.itemSeq || item.id;
            return !existingIds.has(itemId);
          });
          return [...newItems, ...prev];
        });
        setParams(prev => ({
          ...prev,
          offset: newOffset,
          areMoreItemsAvailable: prev.areMoreItemsAvailable,
        }));
        
        // Maintain scroll position after prepending items
        if (container) {
          // Use requestAnimationFrame to ensure DOM has updated
          requestAnimationFrame(() => {
            const newScrollHeight = container.scrollHeight;
            const scrollDifference = newScrollHeight - previousScrollHeight;
            container.scrollTop = container.scrollTop + scrollDifference;
          });
        }
      }
    } catch (error) {
      console.error("Error fetching previous items:", error);
    } finally {
      setIsLoadingPrevious(false);
      fetchingPreviousRef.current = false;
    }
  }, [params.offset, params.limit, isLoadingPrevious, isLoading, filterStatus, fromDate, toDate]);

  // Scroll handler for infinite scroll with debouncing
  const handleScroll = useCallback((e) => {
    const container = scrollContainerRef.current || e.target;
    if (!container) return;
    
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    
    // Clear previous timer
    if (scrollTimerRef.current) {
      clearTimeout(scrollTimerRef.current);
    }
    
    // Debounce scroll handler to avoid excessive API calls
    scrollTimerRef.current = setTimeout(() => {
      // Threshold for triggering load (200px from top/bottom)
      const threshold = 200;
      
      // Load more items when scrolling down (near bottom)
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      if (distanceFromBottom < threshold) {
        if (params.areMoreItemsAvailable && !isLoadingMore && !isLoading && !fetchingMoreRef.current) {
          fetchMoreItems();
        }
      }
      
      // Load previous items when scrolling up (near top)
      if (scrollTop < threshold) {
        if (params.offset > 0 && !isLoadingPrevious && !isLoading && !fetchingPreviousRef.current) {
          fetchPreviousItems();
        }
      }
    }, 100); // 100ms debounce
  }, [params.offset, params.areMoreItemsAvailable, isLoadingMore, isLoadingPrevious, isLoading, fetchMoreItems, fetchPreviousItems]);

  // Fetch items when component mounts or filter status changes
  useEffect(() => {
    setParams(prev => ({
      ...prev,
      offset: 0,
    }));
    fetchItems(filterStatus, true);
  }, [filterStatus]);

  // Attach scroll listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (scrollTimerRef.current) {
        clearTimeout(scrollTimerRef.current);
      }
    };
  }, [handleScroll]);

  // Scroll to grading form when it opens
  useEffect(() => {
    if (expandedItem) {
      // Use setTimeout to ensure DOM has updated after state change
      setTimeout(() => {
        if (expandedGradingFormRef.current) {
          const container = scrollContainerRef.current;
          if (container) {
            // Calculate position relative to scroll container
            const formRect = expandedGradingFormRef.current.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            
            // Calculate the position we need to scroll to
            const scrollTop = container.scrollTop + formRect.top - containerRect.top - 20; // 20px padding
            
            container.scrollTo({
              top: scrollTop,
              behavior: 'smooth'
            });
          } else {
            // Fallback to window scroll if container doesn't exist
            expandedGradingFormRef.current.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'nearest',
              inline: 'nearest'
            });
          }
        }
      }, 100);
    }
  }, [expandedItem]);

  // Fetch stats on initial mount
  useEffect(() => {
    fetchStats();
  }, []);

  // Fetch items and stats when date filters change
  useEffect(() => {
    if (!fromDate && !toDate) {
      return;
    }
    const timeoutId = setTimeout(() => {
      setParams(prev => ({
        ...prev,
        offset: 0,
      }));
      fetchItems(filterStatus, true);
      fetchStats();
    }, 200);
    return () => clearTimeout(timeoutId);
  }, [fromDate, toDate]);

  const handleRejectItem = async (itemId) => {
    setIsGrading(true);
    
    try {
      // Call update item API to reject the item
      await putData(UPDATE_VIRTUAL_GRADING_ITEM_URL, {
        itemId: itemId,
        status: 3, // 3 = rejected
      });

      // Close the grading form
      setExpandedItem(null);
      
      // Reset grading form data
      setGradingData({
        assessedValue: "",
        gradingNotes: "",
        grade: "A",
      });
      setPriceInput("");

      // Refetch items and stats
      fetchItems(filterStatus, false);
      fetchStats();
      
      setIsGrading(false);
    } catch (error) {
      console.error("Error rejecting item:", error);
      setIsGrading(false);
      // TODO: Show error toast/notification
    }
  };

  if (hierarchy !== 1) {
    return null; // Will redirect
  }

  return (
    <PageLayout 
      heading="Virtual Grading Admin"
      icon={UploadIcon}
    >
      <div className="virtual-grading-container">
        {/* Admin Statistics */}
        <div className="vg-stats-grid">
          <div className="vg-stat-card">
            <div className="vg-stat-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="currentColor"
                viewBox="0 0 16 16"
              >
                <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                <path d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708l3-3z" />
              </svg>
            </div>
            <div className="vg-stat-content">
              <div className="vg-stat-label">All</div>
              <div className="vg-stat-value">{stats.total}</div>
            </div>
          </div>

          <div className="vg-stat-card">
            <div className="vg-stat-icon pending">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="currentColor"
                viewBox="0 0 16 16"
              >
                <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z" />
                <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z" />
              </svg>
            </div>
            <div className="vg-stat-content">
              <div className="vg-stat-label">Pending Queue</div>
              <div className="vg-stat-value">{stats.pending}</div>
            </div>
          </div>

          <div className="vg-stat-card">
            <div className="vg-stat-icon success">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="currentColor"
                viewBox="0 0 16 16"
              >
                <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.061L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
              </svg>
            </div>
            <div className="vg-stat-content">
              <div className="vg-stat-label">Items Graded</div>
              <div className="vg-stat-value">{stats.graded}</div>
            </div>
          </div>

          <div className="vg-stat-card">
            <div className="vg-stat-icon info">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="currentColor"
                viewBox="0 0 16 16"
              >
                <path d="M12.136.326A1.5 1.5 0 0 1 14 1.78V3h.5A1.5 1.5 0 0 1 16 4.5v9a1.5 1.5 0 0 1-1.5 1.5H15v1a1.5 1.5 0 0 1-1.5 1.5H1.5A1.5 1.5 0 0 1 0 15.5v-9A1.5 1.5 0 0 1 1.5 5H2V1.78a1.5 1.5 0 0 1 1.864-1.454L12.136.326zM5.562 3H13V1.78a.5.5 0 0 0-.621-.484L5.562 3zM1.5 6a.5.5 0 0 0-.5.5v9a.5.5 0 0 0 .5.5h13a.5.5 0 0 0 .5-.5v-9a.5.5 0 0 0-.5-.5H1.5z" />
                <path d="M8 8a.5.5 0 0 1 .5.5v1.5H10a.5.5 0 0 1 0 1H8.5V12a.5.5 0 0 1-1 0v-1.5H6a.5.5 0 0 1 0-1h1.5V8.5A.5.5 0 0 1 8 8z" />
              </svg>
            </div>
            <div className="vg-stat-content">
              <div className="vg-stat-label">Total Earnings</div>
              <div className="vg-stat-value">${stats.totalValue}</div>
            </div>
          </div>

          <div className="vg-stat-card">
            <div className="vg-stat-icon info">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="currentColor"
                viewBox="0 0 16 16"
              >
                <path d="M1 11a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1v-3zm5-4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V7zm5-5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1V2z" />
              </svg>
            </div>
            <div className="vg-stat-content">
              <div className="vg-stat-label">Avg. Earnings</div>
              <div className="vg-stat-value vg-price">${Math.round(stats.avgPrice)}</div>
            </div>
          </div>

        </div>

        {/* Filters */}
        <div className="vg-filter-bar">
          <div className="vg-filter-content">
            <div className="vg-filter-left">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                fill="currentColor"
                viewBox="0 0 16 16"
                className="mr-8"
              >
                <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z" />
              </svg>
              <span className="mr-12">Filter by Date:</span>
              <input
                type="date"
                className="vg-date-input"
                placeholder="From Date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
              <span className="mx-8">to</span>
              <input
                type="date"
                className="vg-date-input"
                placeholder="To Date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
              <button
                className="vg-reload-button"
                onClick={() => {
                  // Reset offset when reloading
                  setParams(prev => ({
                    ...prev,
                    offset: 0,
                  }));
                  fetchItems(filterStatus, true);
                  fetchStats();
                }}
                title="Reload"
              >
                <img src={RefreshIcon} alt="Reload" className="vg-reload-icon" />
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="vg-tabs">
          <button
            className={`vg-tab ${filterStatus === "pending" ? "active" : ""}`}
            onClick={() => setFilterStatus("pending")}
          >
            Pending Queue ({stats.pending})
          </button>
          <button
            className={`vg-tab ${filterStatus === "graded" ? "active" : ""}`}
            onClick={() => setFilterStatus("graded")}
          >
            Recently Graded ({stats.graded})
          </button>
          <button
            className={`vg-tab ${filterStatus === "rejected" ? "active" : ""}`}
            onClick={() => setFilterStatus("rejected")}
          >
            Rejected ({stats.rejected})
          </button>
          <button
            className={`vg-tab ${filterStatus === "all" ? "active" : ""}`}
            onClick={() => setFilterStatus("all")}
          >
            All ({stats.total})
          </button>
          <button
            className={`vg-tab ${filterStatus === "analytics" ? "active" : ""}`}
            onClick={() => setFilterStatus("analytics")}
          >
            Analytics
          </button>
        </div>

        {/* Content Area */}
        <div 
          className="vg-content" 
          ref={scrollContainerRef}
          style={{ overflowY: 'auto', height: 'calc(100vh - 280px)' }}
        >
          {filterStatus === "pending" && (
            <div className="vg-pending-items">
              {isLoading ? (
                <div className="vg-empty-state">
                  <p>Loading...</p>
                </div>
              ) : items.length === 0 ? (
                <div className="vg-empty-state">
                  <p>No items found</p>
                </div>
              ) : (
                <>
                {items.map((item) => {
                  const itemId = item.itemId || item.id;
                  const status = item.status;
                  const statusString = status === 1 ? "pending" : status === 2 ? "graded" : status === 3 ? "rejected" : "pending";
                  
                  return (
                    <div 
                      key={itemId} 
                      className="vg-admin-item-card"
                    >
                      <div className="vg-item-card-header">
                        <div className="vg-item-title-section">
                          <h4 className="vg-item-title">{item.title || "Untitled Item"}</h4>
                          <div className="vg-item-id-and-customer">
                            <span className="vg-item-id">ID: {item.itemSeq || itemId}</span>
                            {item.companyName && <span className="vg-customer-name">{item.companyName}</span>}
                            {item.username && <span className="vg-customer-username">{item.username}</span>}
                          </div>
                        </div>
                        <div className="vg-item-status-section">
                          <span
                            className="vg-status-badge"
                            style={{ backgroundColor: getStatusColor(status) }}
                          >
                            {statusString.toUpperCase()}
                          </span>
                      <button 
                        className="vg-action-btn chat"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Handle chat functionality
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="10"
                          height="10"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                        >
                          <path d="M14 1a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H4.414A2 2 0 0 0 3 11.586l-2 2V2a1 1 0 0 1 1-1h12zM2 0a2 2 0 0 0-2 2v12.793a.5.5 0 0 0 .854.353l2.853-2.853A1 1 0 0 1 4.414 12H14a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2z" />
                        </svg>
                      </button>
                        </div>
                      </div>
                  
                        <div 
                          className="vg-item-details vg-clickable-section"
                          onClick={() => history.push(`/virtual-grading/item/${itemId}`)}
                        >
                          <div className="vg-detail-row">
                            <span className="vg-detail-label">Make:</span> <span className="vg-detail-value">{item.make || "N/A"}</span>
                          </div>
                          <div className="vg-detail-row">
                            <span className="vg-detail-label">Model:</span> <span className="vg-detail-value">{item.model || "N/A"}</span>
                          </div>
                          <div className="vg-detail-row">
                            <span className="vg-detail-label">Year:</span> <span className="vg-detail-value">{item.year || "N/A"}</span>
                          </div>
                          <div className="vg-detail-row">
                            <span className="vg-detail-label">Part No:</span> <span className="vg-detail-value">{item.partNo || item.partNumber || "N/A"}</span>
                          </div>
                        </div>
                        
                        <div 
                          className="vg-item-image-section vg-clickable-section"
                          onClick={(e) => openImagePreview(e, item)}
                          title="Click to expand"
                        >
                          <img
                            src={getItemImageUrl(item)}
                            alt={item.title || "Item"}
                            className="vg-item-main-image"
                          />
                          <span className="vg-image-expand-hint" aria-hidden="true">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                              <path d="M1.5 1a.5.5 0 0 0-.5.5v4a.5.5 0 0 1-1 0v-4A1.5 1.5 0 0 1 1.5 0h4a.5.5 0 0 1 0 1h-4zM10 .5a.5.5 0 0 1 .5-.5h4A1.5 1.5 0 0 1 16 1.5v4a.5.5 0 0 1-1 0v-4a.5.5 0 0 0-.5-.5h-4a.5.5 0 0 1-.5-.5zM.5 10a.5.5 0 0 1 .5.5v4a.5.5 0 0 0 .5.5h4a.5.5 0 0 1 0 1h-4A1.5 1.5 0 0 1 0 14.5v-4a.5.5 0 0 1 .5-.5zm15 0a.5.5 0 0 1 .5.5v4a1.5 1.5 0 0 1-1.5 1.5h-4a.5.5 0 0 1 0-1h4a.5.5 0 0 0 .5-.5v-4a.5.5 0 0 1 .5-.5z"/>
                            </svg>
                          </span>
                        </div>
                        
                        {item.createdDate && (
                          <div className="vg-item-timestamp">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="currentColor"
                              viewBox="0 0 16 16"
                              className="mr-4"
                            >
                              <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z" />
                              <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z" />
                            </svg>
                            {getTimeElapsed(item.createdDate)}
                          </div>
                        )}
                        
                        <div className="vg-item-actions">
                          <button 
                            className="vg-action-btn primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStartGrading(itemId);
                            }}
                          >
                            Start Grading
                          </button>
                        </div>
                  
                        {expandedItem === itemId && (
                          <div 
                            className="vg-grading-expanded" 
                            ref={expandedGradingFormRef}
                          >
                            <div className="vg-grading-form">
                      <div className="vg-form-group">
                        <label className="vg-form-label">Assessed Value ($) <span style={{color: '#999', fontSize: '12px', fontWeight: 'normal'}}>(or Grade)</span></label>
                        <input
                          type="text"
                          className="vg-form-input"
                          value={priceInput}
                          onChange={(e) => handlePriceInputChange(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          placeholder="Enter price"
                          autoComplete="off"
                          disabled={gradeInput.trim() !== "" || selectedGradeItem !== null}
                        />
                      </div>
                      
                      <div className="vg-form-group">
                        <label className="vg-form-label">Grade <span style={{color: '#999', fontSize: '12px', fontWeight: 'normal'}}>(or Assessed Value)</span></label>
                        <div className="vg-price-input-container" ref={gradeInputRef}>
                          <input
                            type="text"
                            className="vg-form-input vg-price-input"
                            value={gradeInput}
                            onChange={(e) => handleGradeInputChange(e.target.value)}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (gradeInput.length > 0) {
                                searchGradeProducts(gradeInput);
                              }
                            }}
                            placeholder="Enter grade or search for suggestions..."
                            autoComplete="off"
                            disabled={priceInput.trim() !== ""}
                          />
                          {isLoadingGradeSuggestions && (
                            <div className="vg-loading-spinner">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                fill="currentColor"
                                viewBox="0 0 16 16"
                                className="vg-spinner"
                              >
                                <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zM7.5 4a.5.5 0 0 1 .5.5v3a.5.5 0 0 1-1 0v-3a.5.5 0 0 1 .5-.5z" />
                              </svg>
                            </div>
                          )}
                          {showGradeSuggestions && gradeSuggestions.length > 0 && (
                            <div className="vg-suggestions-dropdown">
                              {gradeSuggestions.map((item, index) => (
                                <div
                                  key={item.productId || index}
                                  className="vg-suggestion-item"
                                  onClick={() => handleGradeItemSelect(item)}
                                >
                                  <div className="vg-suggestion-price">{item.oem}</div>
                                  {item.basePrice && (
                                    <div className="vg-suggestion-base-price">${item.basePrice}</div>
                                  )}
                                  <div className="vg-suggestion-description">{item.description}</div>
                                  {item.urls && item.urls.length > 0 && (
                                    <div className="ht-30 wt-30 ml-12">
                                      <img
                                        src={item.urls[0]}
                                        className="ht-30 wt-30"
                                        alt="Product"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          // TODO: Handle image preview
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        {selectedGradeItem && (
                          <div className="vg-selected-suggestion">
                            <div className="vg-selected-suggestion-content">
                              <span className="vg-selected-label">Selected:</span>
                              <span className="vg-selected-price">{selectedGradeItem.oem}</span>
                              {selectedGradeItem.basePrice && (
                                <span className="vg-selected-price" style={{ marginLeft: '8px' }}>${selectedGradeItem.basePrice}</span>
                              )}
                              <span className="vg-selected-desc">{selectedGradeItem.description}</span>
                            </div>
                            <button
                              type="button"
                              className="vg-clear-grade-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleClearGrade();
                              }}
                              title="Clear selected grade"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="14"
                                fill="currentColor"
                                viewBox="0 0 16 16"
                              >
                                <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                      
                      <div className="vg-form-group">
                        <label className="vg-form-label">Grading Notes</label>
                        <textarea
                          className="vg-form-textarea"
                          rows="4"
                          value={gradingData.gradingNotes}
                          onChange={(e) => handleGradingChange("gradingNotes", e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          placeholder="Add detailed notes about the item's condition, any issues, or grading rationale..."
                        />
                      </div>
                      
                      <div className="vg-grading-actions">
                        <button
                          className="vg-submit-grade-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSubmitGrade(itemId);
                          }}
                          disabled={isGrading || (!(priceInput && priceInput.trim() !== "") && !selectedGradeItem && !(gradeInput && gradeInput.trim() !== ""))}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            viewBox="0 0 16 16"
                            className="mr-8"
                          >
                            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                            <path d="M10.97 4.97a.235.235 0 0 0-.02.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.061L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
                          </svg>
                          Submit Grade
                        </button>
                        <button
                          className="vg-reject-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRejectItem(itemId);
                          }}
                          disabled={isGrading}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            viewBox="0 0 16 16"
                            className="mr-8"
                          >
                            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                            <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                          </svg>
                          Reject
                        </button>
                        <button
                          className="vg-cancel-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCloseGrading();
                          }}
                          disabled={isGrading}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                        )}
                    </div>
                  );
                  })}
                </>
              )}
            </div>
          )}
          
          {filterStatus === "graded" && (
            <div className="vg-pending-items">
              {isLoading ? (
                <div className="vg-empty-state">
                  <p>Loading...</p>
                </div>
              ) : items.length === 0 ? (
                <div className="vg-empty-state">
                  <p>No items found</p>
                </div>
              ) : (
                <>
                {items.map((item) => {
                  const itemId = item.itemId || item.id;
                  const status = item.status;
                  const statusString = status === 1 ? "pending" : status === 2 ? "graded" : status === 3 ? "rejected" : "pending";
                  
                  return (
                    <div 
                      key={itemId} 
                      className="vg-admin-item-card"
                    >
                      <div className="vg-item-card-header">
                        <div className="vg-item-title-section">
                          <h4 className="vg-item-title">{item.title || "Untitled Item"}</h4>
                          <div className="vg-item-id-and-customer">
                            <span className="vg-item-id">ID: {item.itemSeq || itemId}</span>
                            {item.companyName && <span className="vg-customer-name">{item.companyName}</span>}
                            {item.username && <span className="vg-customer-username">{item.username}</span>}
                          </div>
                        </div>
                        <div className="vg-item-status-section">
                          <span
                            className="vg-status-badge"
                            style={{ backgroundColor: getStatusColor(status) }}
                          >
                            {statusString.toUpperCase()}
                          </span>
                          <button 
                            className="vg-action-btn chat"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Handle chat functionality
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="10"
                              height="10"
                              fill="currentColor"
                              viewBox="0 0 16 16"
                            >
                              <path d="M14 1a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H4.414A2 2 0 0 0 3 11.586l-2 2V2a1 1 0 0 1 1-1h12zM2 0a2 2 0 0 0-2 2v12.793a.5.5 0 0 0 .854.353l2.853-2.853A1 1 0 0 1 4.414 12H14a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      
                      <div 
                        className="vg-item-details vg-clickable-section"
                        onClick={() => history.push(`/virtual-grading/item/${itemId}`)}
                      >
                        <div className="vg-detail-row">
                          <span className="vg-detail-label">Make:</span>
                          <span className="vg-detail-value">{item.make || "N/A"}</span>
                        </div>
                        <div className="vg-detail-row">
                          <span className="vg-detail-label">Model:</span>
                          <span className="vg-detail-value">{item.model || "N/A"}</span>
                        </div>
                        <div className="vg-detail-row">
                          <span className="vg-detail-label">Year:</span>
                          <span className="vg-detail-value">{item.year || "N/A"}</span>
                        </div>
                        <div className="vg-detail-row">
                          <span className="vg-detail-label">Part No:</span>
                          <span className="vg-detail-value">{item.partNo || item.partNumber || "N/A"}</span>
                        </div>
                      </div>
                      
                      <div 
                        className="vg-item-image-section vg-clickable-section"
                        onClick={(e) => openImagePreview(e, item)}
                        title="Click to expand"
                      >
                        <img
                          src={getItemImageUrl(item)}
                          alt={item.title || "Item"}
                          className="vg-item-main-image"
                        />
                        <span className="vg-image-expand-hint" aria-hidden="true">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M1.5 1a.5.5 0 0 0-.5.5v4a.5.5 0 0 1-1 0v-4A1.5 1.5 0 0 1 1.5 0h4a.5.5 0 0 1 0 1h-4zM10 .5a.5.5 0 0 1 .5-.5h4A1.5 1.5 0 0 1 16 1.5v4a.5.5 0 0 1-1 0v-4a.5.5 0 0 0-.5-.5h-4a.5.5 0 0 1-.5-.5zM.5 10a.5.5 0 0 1 .5.5v4a.5.5 0 0 0 .5.5h4a.5.5 0 0 1 0 1h-4A1.5 1.5 0 0 1 0 14.5v-4a.5.5 0 0 1 .5-.5zm15 0a.5.5 0 0 1 .5.5v4a1.5 1.5 0 0 1-1.5 1.5h-4a.5.5 0 0 1 0-1h4a.5.5 0 0 0 .5-.5v-4a.5.5 0 0 1 .5-.5z"/>
                          </svg>
                        </span>
                      </div>
                      
                      {item.createdDate && (
                        <div className="vg-item-timestamp">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            viewBox="0 0 16 16"
                            className="mr-4"
                          >
                            <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z" />
                            <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z" />
                          </svg>
                          {getTimeElapsed(item.createdDate)}
                        </div>
                      )}
                      
                      {(status === 2) && (item.grade || item.price || item.assessedValue) && (
                        <div className="vg-item-grade-info">
                          {item.grade && <span className="vg-grade-badge">Grade: {item.grade}</span>}
                          {(item.price || item.assessedValue || item.gradePrice) && (
                            <span className="vg-price-badge">${item.price || item.assessedValue || item.gradePrice || 0}</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                  })}
                </>
              )}
              
              {/* Loading indicators for infinite scroll */}
              {isLoadingPrevious && (
                <div className="vg-loading-indicator" style={{ textAlign: 'center', padding: '10px' }}>
                  Loading previous items...
                </div>
              )}
              {isLoadingMore && (
                <div className="vg-loading-indicator" style={{ textAlign: 'center', padding: '10px' }}>
                  Loading more items...
                </div>
              )}
            </div>
          )}
          
          {filterStatus === "rejected" && (
            <div className="vg-pending-items">
              {isLoading ? (
                <div className="vg-empty-state">
                  <p>Loading...</p>
                </div>
              ) : items.length === 0 ? (
                <div className="vg-empty-state">
                  <p>No items found</p>
                </div>
              ) : (
                <>
                {items.map((item) => {
                  const itemId = item.itemId || item.id;
                  const status = item.status;
                  const statusString = status === 1 ? "pending" : status === 2 ? "graded" : status === 3 ? "rejected" : "pending";
                  
                  return (
                    <div 
                      key={itemId} 
                      className="vg-admin-item-card"
                    >
                      <div className="vg-item-card-header">
                        <div className="vg-item-title-section">
                          <h4 className="vg-item-title">{item.title || "Untitled Item"}</h4>
                          <div className="vg-item-id-and-customer">
                            <span className="vg-item-id">ID: {item.itemSeq || itemId}</span>
                            {item.companyName && <span className="vg-customer-name">{item.companyName}</span>}
                            {item.username && <span className="vg-customer-username">{item.username}</span>}
                          </div>
                        </div>
                        <div className="vg-item-status-section">
                          <span
                            className="vg-status-badge"
                            style={{ backgroundColor: getStatusColor(status) }}
                          >
                            {statusString.toUpperCase()}
                          </span>
                          <button 
                            className="vg-action-btn chat"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Handle chat functionality
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="10"
                              height="10"
                              fill="currentColor"
                              viewBox="0 0 16 16"
                            >
                              <path d="M14 1a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H4.414A2 2 0 0 0 3 11.586l-2 2V2a1 1 0 0 1 1-1h12zM2 0a2 2 0 0 0-2 2v12.793a.5.5 0 0 0 .854.353l2.853-2.853A1 1 0 0 1 4.414 12H14a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      
                      <div 
                        className="vg-item-details vg-clickable-section"
                        onClick={() => history.push(`/virtual-grading/item/${itemId}`)}
                      >
                        <div className="vg-detail-row">
                          <span className="vg-detail-label">Make:</span>
                          <span className="vg-detail-value">{item.make || "N/A"}</span>
                        </div>
                        <div className="vg-detail-row">
                          <span className="vg-detail-label">Model:</span>
                          <span className="vg-detail-value">{item.model || "N/A"}</span>
                        </div>
                        <div className="vg-detail-row">
                          <span className="vg-detail-label">Year:</span>
                          <span className="vg-detail-value">{item.year || "N/A"}</span>
                        </div>
                        <div className="vg-detail-row">
                          <span className="vg-detail-label">Part No:</span>
                          <span className="vg-detail-value">{item.partNo || item.partNumber || "N/A"}</span>
                        </div>
                      </div>
                      
                      <div 
                        className="vg-item-image-section vg-clickable-section"
                        onClick={(e) => openImagePreview(e, item)}
                        title="Click to expand"
                      >
                        <img
                          src={getItemImageUrl(item)}
                          alt={item.title || "Item"}
                          className="vg-item-main-image"
                        />
                        <span className="vg-image-expand-hint" aria-hidden="true">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M1.5 1a.5.5 0 0 0-.5.5v4a.5.5 0 0 1-1 0v-4A1.5 1.5 0 0 1 1.5 0h4a.5.5 0 0 1 0 1h-4zM10 .5a.5.5 0 0 1 .5-.5h4A1.5 1.5 0 0 1 16 1.5v4a.5.5 0 0 1-1 0v-4a.5.5 0 0 0-.5-.5h-4a.5.5 0 0 1-.5-.5zM.5 10a.5.5 0 0 1 .5.5v4a.5.5 0 0 0 .5.5h4a.5.5 0 0 1 0 1h-4A1.5 1.5 0 0 1 0 14.5v-4a.5.5 0 0 1 .5-.5zm15 0a.5.5 0 0 1 .5.5v4a1.5 1.5 0 0 1-1.5 1.5h-4a.5.5 0 0 1 0-1h4a.5.5 0 0 0 .5-.5v-4a.5.5 0 0 1 .5-.5z"/>
                          </svg>
                        </span>
                      </div>
                      
                      {item.createdDate && (
                        <div className="vg-item-timestamp">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            viewBox="0 0 16 16"
                            className="mr-4"
                          >
                            <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z" />
                            <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z" />
                          </svg>
                          {getTimeElapsed(item.createdDate)}
                        </div>
                      )}
                    </div>
                  );
                  })}
                </>
              )}
            </div>
          )}
          
          {filterStatus === "all" && (
            <div className="vg-pending-items">
              {isLoading ? (
                <div className="vg-empty-state">
                  <p>Loading...</p>
                </div>
              ) : items.length === 0 ? (
                <div className="vg-empty-state">
                  <p>No items found</p>
                </div>
              ) : (
                <>
                {items.map((item) => {
                  const itemId = item.itemId || item.id;
                  const status = item.status;
                  const statusString = status === 1 ? "pending" : status === 2 ? "graded" : status === 3 ? "rejected" : "pending";
                  
                  return (
                    <div 
                      key={itemId} 
                      className="vg-admin-item-card"
                    >
                      <div className="vg-item-card-header">
                        <div className="vg-item-title-section">
                          <h4 className="vg-item-title">{item.title || "Untitled Item"}</h4>
                          <div className="vg-item-id-and-customer">
                            <span className="vg-item-id">ID: {item.itemSeq || itemId}</span>
                            {item.companyName && <span className="vg-customer-name">{item.companyName}</span>}
                            {item.username && <span className="vg-customer-username">{item.username}</span>}
                          </div>
                        </div>
                        <div className="vg-item-status-section">
                          <span
                            className="vg-status-badge"
                            style={{ backgroundColor: getStatusColor(status) }}
                          >
                            {statusString.toUpperCase()}
                          </span>
                          <button 
                            className="vg-action-btn chat"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Handle chat functionality
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="10"
                              height="10"
                              fill="currentColor"
                              viewBox="0 0 16 16"
                            >
                              <path d="M14 1a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H4.414A2 2 0 0 0 3 11.586l-2 2V2a1 1 0 0 1 1-1h12zM2 0a2 2 0 0 0-2 2v12.793a.5.5 0 0 0 .854.353l2.853-2.853A1 1 0 0 1 4.414 12H14a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      
                      <div 
                        className="vg-item-details vg-clickable-section"
                        onClick={() => history.push(`/virtual-grading/item/${itemId}`)}
                      >
                        <div className="vg-detail-row">
                          <span className="vg-detail-label">Make:</span>
                          <span className="vg-detail-value">{item.make || "N/A"}</span>
                        </div>
                        <div className="vg-detail-row">
                          <span className="vg-detail-label">Model:</span>
                          <span className="vg-detail-value">{item.model || "N/A"}</span>
                        </div>
                        <div className="vg-detail-row">
                          <span className="vg-detail-label">Year:</span>
                          <span className="vg-detail-value">{item.year || "N/A"}</span>
                        </div>
                        <div className="vg-detail-row">
                          <span className="vg-detail-label">Part No:</span>
                          <span className="vg-detail-value">{item.partNo || item.partNumber || "N/A"}</span>
                        </div>
                      </div>
                      
                      <div 
                        className="vg-item-image-section vg-clickable-section"
                        onClick={(e) => openImagePreview(e, item)}
                        title="Click to expand"
                      >
                        <img
                          src={getItemImageUrl(item)}
                          alt={item.title || "Item"}
                          className="vg-item-main-image"
                        />
                        <span className="vg-image-expand-hint" aria-hidden="true">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M1.5 1a.5.5 0 0 0-.5.5v4a.5.5 0 0 1-1 0v-4A1.5 1.5 0 0 1 1.5 0h4a.5.5 0 0 1 0 1h-4zM10 .5a.5.5 0 0 1 .5-.5h4A1.5 1.5 0 0 1 16 1.5v4a.5.5 0 0 1-1 0v-4a.5.5 0 0 0-.5-.5h-4a.5.5 0 0 1-.5-.5zM.5 10a.5.5 0 0 1 .5.5v4a.5.5 0 0 0 .5.5h4a.5.5 0 0 1 0 1h-4A1.5 1.5 0 0 1 0 14.5v-4a.5.5 0 0 1 .5-.5zm15 0a.5.5 0 0 1 .5.5v4a1.5 1.5 0 0 1-1.5 1.5h-4a.5.5 0 0 1 0-1h4a.5.5 0 0 0 .5-.5v-4a.5.5 0 0 1 .5-.5z"/>
                          </svg>
                        </span>
                      </div>
                      
                      {item.createdDate && (
                        <div className="vg-item-timestamp">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            viewBox="0 0 16 16"
                            className="mr-4"
                          >
                            <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z" />
                            <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z" />
                          </svg>
                          {getTimeElapsed(item.createdDate)}
                        </div>
                      )}
                      
                      {(status === 2) && (item.grade || item.price || item.assessedValue) && (
                        <div className="vg-item-grade-info">
                          {item.grade && <span className="vg-grade-badge">Grade: {item.grade}</span>}
                          {(item.price || item.assessedValue || item.gradePrice) && (
                            <span className="vg-price-badge">${item.price || item.assessedValue || item.gradePrice || 0}</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                  })}
                </>
              )}
            </div>
          )}
          
          {filterStatus === "analytics" && (
            <div className="vg-analytics">
              <p>Analytics dashboard will appear here</p>
            </div>
          )}
          
          {/* Pagination */}
        </div>
      </div>
      {previewImage && (
        <ImageLightbox
          src={previewImage.src}
          alt={previewImage.alt}
          onClose={() => setPreviewImage(null)}
        />
      )}
    </PageLayout>
  );
};

export default AdminDashboard;
